import { ArrowRight } from 'lucide-react'
import { ReactNode, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { cn } from '@/lib/utils'
import { AdjustReason, adjustReasons, formatQuantity } from '@/lib/warehouse/stockLevels'
import { components } from '@invo.space/shared'

interface StockAdjustPopoverProps {
  stockItem: StockItem
  onSuccess?: () => void
  children: ReactNode
  align?: 'start' | 'center' | 'end'
}

const inputLabel: Record<string, string> = {
  add: 'Přidat kusů',
  remove: 'Odebrat kusů',
  delta: 'Změna (+/-)',
  count: 'Napočítáno kusů',
}

/** Úprava stavu zásoby s důvodem a náhledem "100 → 110" (Shopify). */
export const StockAdjustPopover = ({ stockItem, onSuccess, children, align = 'end' }: StockAdjustPopoverProps) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [reason, setReason] = useState<AdjustReason>('receipt')
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const meta = adjustReasons[reason]
  const current = stockItem.isUnlimited ? 0 : stockItem.quantityOnHand
  const parsed = value.trim() === '' ? null : Number(value.replace(',', '.'))
  const valid = parsed !== null && Number.isInteger(parsed)

  let delta: number | null = null
  if (valid) {
    if (meta.input === 'add') delta = Math.abs(parsed)
    else if (meta.input === 'remove') delta = -Math.abs(parsed)
    else if (meta.input === 'delta') delta = parsed
    else delta = parsed - current
  }
  const next = delta === null ? null : current + delta
  const canSave =
    delta !== null &&
    delta !== 0 &&
    (meta.input === 'count' ? parsed! >= 0 : true) &&
    (meta.input === 'add' || meta.input === 'remove' ? parsed! > 0 : true)

  const reset = () => {
    setReason('receipt')
    setValue('')
    setNote('')
  }

  const save = async () => {
    if (!canSave || delta === null) return
    setSaving(true)
    try {
      const payload: components['schemas']['StockMoveCreate'] = {
        warehouseId: stockItem.warehouseId,
        batchNumber: stockItem.batchNumber ?? null,
        type: meta.type,
        quantity: meta.type === 'adjustment' ? delta : Math.abs(delta),
        reason,
        note: note.trim() || null,
      }
      await API.post(`/api/v2/moves/${stockItem.skuId}`, payload)
      toast.success('Stav zásoby upraven')
      setOpen(false)
      reset()
      onSuccess?.()
    } catch (error: unknown) {
      toast.error('Stav se nepodařilo upravit', { description: extractErrorMessage(error) || undefined })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) reset()
      }}
    >
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
        >
          <div className="border-b px-4 py-3">
            <p className="text-sm font-medium">Upravit stav</p>
            <p className="truncate text-xs text-muted-foreground">
              {stockItem.skuName || stockItem.skuCode}, {stockItem.warehouseName}
            </p>
          </div>

          <div className="space-y-3 px-4 py-3">
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Důvod</span>
              <Select
                value={reason}
                onValueChange={(v) => {
                  setReason(v as AdjustReason)
                  inputRef.current?.focus()
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(adjustReasons) as AdjustReason[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {adjustReasons[key].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">{inputLabel[meta.input]}</span>
              <Input
                ref={inputRef}
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={meta.input === 'count' ? String(current) : '0'}
                className="tabular"
              />
            </label>

            <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">Skladem</span>
              <span className="flex items-center gap-2 tabular">
                <span className="text-muted-foreground">
                  {stockItem.isUnlimited ? 'Neomezeno' : formatQuantity(current)}
                </span>
                <ArrowRight className="size-3.5 text-muted-foreground" />
                <span className={cn('font-medium', next !== null && next < 0 && 'text-danger')}>
                  {next === null ? '-' : formatQuantity(next)}
                </span>
                {delta !== null && delta !== 0 && (
                  <span className={cn('text-xs', delta > 0 ? 'text-success' : 'text-danger')}>
                    ({delta > 0 ? '+' : '-'}
                    {formatQuantity(Math.abs(delta))})
                  </span>
                )}
              </span>
            </div>

            {stockItem.isUnlimited && delta !== null && delta > 0 && (
              <p className="text-xs text-muted-foreground">Po uložení se začne zásoba sledovat po kusech.</p>
            )}

            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Poznámka</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Volitelné" />
            </label>
          </div>

          <div className="flex justify-end gap-2 border-t px-4 py-3">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Zrušit
            </Button>
            <Button type="submit" size="sm" disabled={!canSave || saving}>
              Uložit
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
